import { useTranslation } from 'react-i18next'

import { formatRejectionReason, getCurrentStateBadge } from '../../utils/utils'

import { Alert, Avatar, Badge, Button, ButtonText, Flex, Heading, Tabs, theme } from '@percent/lemonade'
import { CurrentStateLabel, EntryType } from '@percent/partner-dashboard/services/grants/grantsService.types'
import styles from './CandidateHeader.module.scss'
import type { CandidateHeaderProps, CandidateHeaderTabs } from './CandidateHeader.types'
import { CandidateHeaderTab } from './CandidateHeader.types'
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlag } from '@percent/partner-dashboard/common/hooks'

export function CandidateHeader({
  candidate,
  onOpenTasksView,
  isTasksViewOpen,
  onShowComments,
  commentsShown,
  activeTab,
  setActiveTab,
  hasTasks,
  totalComments,
  totalNominations,
  totalDocuments,
  formSubmissions,
  showDocumentsTab,
  entryType
}: Readonly<CandidateHeaderProps>) {
  const { t } = useTranslation()
  const timelineFeatureFlag = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.PD_GRANTS_CANDIDATE_TIMELINE)

  const { organisation, state } = candidate
  const { label: currentState, reason: rejectionReason, rejectionReasonDetail } = state

  const stateBadge = getCurrentStateBadge({ currentState, rejectionReason, t })
  const partnerSubmissions =
    formSubmissions.filter(submission => !submission.audience || submission.audience === 'partner')?.length || 0
  const organisationSubmissions =
    formSubmissions.filter(submission => submission.audience === 'organisation')?.length || 0

  const getEntryTypeTab = (): CandidateHeaderTabs => {
    switch (entryType) {
      case EntryType.APPLICATION:
        return [{ label: t('grants.candidateDetails.header.applicationTab'), key: CandidateHeaderTab.APPLICATION }]
      case EntryType.NOMINATION:
      case EntryType.INVITATION:
        return [
          {
            label: t('grants.candidateDetails.header.nominationsTab', { count: totalNominations }),
            key: CandidateHeaderTab.NOMINATIONS
          }
        ]
      default:
        return []
    }
  }

  const tabs: CandidateHeaderTabs = [
    {
      label: t('grants.candidateDetails.header.organizationTab'),
      key: CandidateHeaderTab.ORGANIZATION_DETAILS
    },
    ...getEntryTypeTab(),
    {
      label: t('grants.candidateDetails.header.submittedInfoTab', {
        count: organisationSubmissions
      }),
      key: CandidateHeaderTab.SUBMITTED_INFO
    },
    ...(showDocumentsTab
      ? [
          {
            label: t('grants.candidateDetails.header.documentsTab', {
              count: totalDocuments
            }),
            key: CandidateHeaderTab.DOCUMENTS
          }
        ]
      : []),
    {
      label: t('grants.candidateDetails.header.reviewsTab', {
        count: partnerSubmissions
      }),
      key: CandidateHeaderTab.REVIEWS
    },
    {
      label: t('grants.candidateDetails.header.paymentTab'),
      key: CandidateHeaderTab.PAYMENT
    },
    ...(timelineFeatureFlag
      ? [{ label: t('grants.candidateDetails.header.timelineTab'), key: CandidateHeaderTab.TIMELINE }]
      : [])
  ]

  return (
    <Flex direction="column" className={styles.header} data-testid="candidate-header">
      <Flex direction="column" justify="flex-start" align="flex-start" className={styles.heading}>
        <Flex align="center" w="100%" gap={theme.sizes.s}>
          <Avatar image={organisation.logo} alt={organisation.name} size="medium" />
          <Flex gap={theme.sizes.xs} align="center">
            <Heading level="h3" data-testid="candidate-header-title">
              {organisation.name}
            </Heading>
            {currentState !== CurrentStateLabel.REJECTED && (
              <Badge variant={stateBadge.variant} icon={stateBadge.icon} testId="candidate-state-badge">
                {stateBadge.label}
              </Badge>
            )}
            {!commentsShown && (
              <ButtonText
                icon="comment"
                onClick={onShowComments}
                className={styles.commentsButton}
                data-testid="comments-button-header"
              >
                {totalComments}
              </ButtonText>
            )}
          </Flex>
          {!isTasksViewOpen && hasTasks && (
            <Flex ml="auto">
              <Button variant="secondary" size="small" onClick={onOpenTasksView} data-testId="open-tasks-button-header">
                {t('grants.tasks.openTasks')}
              </Button>
            </Flex>
          )}
        </Flex>

        {currentState === CurrentStateLabel.REJECTED && (
          <Flex className={styles.rejectionAlertWrapper} data-testid="rejection-alert">
            <Alert
              variant="error"
              title={t('grants.candidateDetails.header.rejectionReason', {
                rejectionReason:
                  formatRejectionReason({ rejectionReason, rejectionReasonDetail }, t) ?? t('typography.notAvailable')
              })}
            />
          </Flex>
        )}
      </Flex>
      <Tabs
        items={tabs}
        activeTab={activeTab}
        onChangeTab={newTab => setActiveTab(newTab as CandidateHeaderTab)}
        scrollable
      />
    </Flex>
  )
}
